import { ContentCard } from '../../styles';
import { Spacer } from '@kizen/kds/Spacer';
import { Button } from '@kizen/kds/Button';
import { useTranslation } from 'react-i18next';
import { Typography } from '@kizen/kds/Typography';
import Loader from '__components/Kizen/Loader';
import RadioButton from '__components/Kizen/RadioButton';
import { ResolvedPlanDetails } from '../hooks/useSelectedPlanResolution';
import { formatBillingCurrency } from '../../util';
import { ensureTrailingPeriod, formatPendingDate } from '../util';
import {
  BillingCadence,
  SummaryLine,
  computeCadenceAmounts,
  planDiscount,
} from './planSummaryShared';
import { UpcomingLines } from './UpcomingLines';

export type { BillingCadence } from './planSummaryShared';

const cadenceLabel = (cadence: BillingCadence, t: (s: string) => string) =>
  cadence === 'year' ? t('Annual') : t('Monthly');

type PlanSummaryCardProps = {
  sectionName: string;
  cadence: BillingCadence;
  showAnnualBilling: boolean;
  corePlatformPlan: ResolvedPlanDetails;
  automationPlan: ResolvedPlanDetails;
  marketingPlan: ResolvedPlanDetails;
  numUsersForCalculation: number;
  proratedAmount?: number | null;
  firstBillingDate?: string;
};

export const PlanSummaryCard = (props: PlanSummaryCardProps) => {
  const {
    sectionName,
    cadence,
    showAnnualBilling,
    corePlatformPlan,
    numUsersForCalculation,
    automationPlan,
    marketingPlan,
    proratedAmount,
    firstBillingDate,
  } = props;

  const { t } = useTranslation();

  const corePerSeatMonthly = corePlatformPlan?.price?.amount ?? 0;
  const automationMonthly = automationPlan?.price?.amount ?? 0;
  const marketingMonthly = marketingPlan?.price?.amount ?? 0;

  const allFree =
    corePerSeatMonthly === 0 &&
    automationMonthly === 0 &&
    marketingMonthly === 0;

  const showPeriod = showAnnualBilling && !allFree;

  const {
    coreLine: coreLineAmount,
    automationLine: automationLineAmount,
    marketingLine: marketingLineAmount,
    total,
  } = computeCadenceAmounts(
    cadence,
    corePerSeatMonthly * numUsersForCalculation,
    planDiscount(corePlatformPlan),
    automationMonthly,
    planDiscount(automationPlan),
    marketingMonthly,
    planDiscount(marketingPlan)
  );

  const hasProration =
    typeof proratedAmount === 'number' && !Number.isNaN(proratedAmount);

  return (
    <div>
      <Typography weight="semibold" size="lg">
        {showPeriod
          ? t('{{sectionName}} Plan Summary ({{period}})', {
              period: cadenceLabel(cadence, t),
              sectionName,
            })
          : t('{{sectionName}} Plan Summary', { sectionName })}
      </Typography>
      <Spacer size={30} />
      <SummaryLine
        label={t('Kizen Native AI Platform - {{planName}}', {
          planName: corePlatformPlan?.name,
        })}
        detail={t('{{userCount}} Users', {
          userCount: numUsersForCalculation?.toLocaleString(),
        })}
        amount={coreLineAmount}
        symbol={corePlatformPlan?.price?.symbol}
      />
      <Spacer size={30} />
      <SummaryLine
        isAdditional
        label={t('Agentic Workflows - {{planName}}', {
          planName: automationPlan?.name,
        })}
        detail={t('{{limit}} Monthly Automated Tasks', {
          limit: automationPlan?.limits?.monthly_tasks_limit?.toLocaleString(),
        })}
        amount={automationLineAmount}
        symbol={automationPlan?.price?.symbol}
      />
      <Spacer size={30} />
      <SummaryLine
        isAdditional
        label={t('Marketing - {{planName}}', {
          planName: marketingPlan?.name,
        })}
        detail={t('{{limit}} Monthly Emails', {
          limit: marketingPlan?.limits?.monthly_emails_limit?.toLocaleString(),
        })}
        amount={marketingLineAmount}
        symbol={marketingPlan?.price?.symbol}
      />
      <Spacer size={30} />
      {hasProration ? (
        <>
          <div>
            <div className="flex items-center">
              <Typography size="lg" weight="semibold">
                {t('Prorated Amount Billed Today')}
              </Typography>
              <Typography className="ml-auto" size="lg" weight="semibold">
                {formatBillingCurrency(proratedAmount ?? 0)}
              </Typography>
            </div>
            {firstBillingDate ? (
              <>
                <Spacer size={10} />
                <Typography size="md">
                  {t('(through {{date}})', {
                    date: formatPendingDate(firstBillingDate),
                  })}
                </Typography>
              </>
            ) : null}
          </div>
          <Spacer size={20} />
        </>
      ) : null}
      <div className="flex items-center">
        <Typography size="lg" weight="semibold">
          {showPeriod
            ? t('{{sectionName}} {{period}} Total', {
                sectionName,
                period: cadenceLabel(cadence, t),
              })
            : t('{{sectionName}} Total', { sectionName })}
        </Typography>
        <Typography className="ml-auto" size="lg" weight="semibold">
          {formatBillingCurrency(total)}
        </Typography>
      </div>
    </div>
  );
};

type PlanSummaryProps = {
  activeCorePlatformPlan: ResolvedPlanDetails;
  activeAutomationPlan: ResolvedPlanDetails;
  activeMarketingPlan: ResolvedPlanDetails;
  currentSelectedCorePlatformPlan: ResolvedPlanDetails;
  currentSelectedAutomationPlan: ResolvedPlanDetails;
  currentSelectedMarketingPlan: ResolvedPlanDetails;
  numUsersForCalculation: number;
  currentNumUsersForCalculation: number;
  pendingNumUsersForCalculation: number;
  annualDiscountPercentage: number;
  showAnnualBilling: boolean;
  currentCadence: BillingCadence;
  upcomingCadence: BillingCadence;
  onUpcomingCadenceChange: (cadence: BillingCadence) => void;
  showUpcoming: boolean;
  canSwitchCadenceOnly: boolean;
  onRequestCadenceChange: () => void;
  planChangeClarification?: string;
  newBillingCycleStartDate?: string;
  proratedAmount?: number | null;
  summaryTotal?: number | null;
  previewedCadence?: BillingCadence | null;
  isDirty: boolean;
  pendingRecalculation: boolean;
  isAwaitingPreview: boolean;
  onCancelUpdate: () => void;
  onUpdatePlan: () => void;
  hasPendingChange: boolean;
  pendingCorePlatformPlan: ResolvedPlanDetails;
  pendingAutomationPlan: ResolvedPlanDetails;
  pendingMarketingPlan: ResolvedPlanDetails;
  pendingPlanDiffers: boolean;
  upcomingPendingHasPaidPlan: boolean;
  onCancelPendingPlan: () => void;
  onChangePendingBillingCadence: () => void;
  pendingEffectiveDate?: string;
  pendingCadence: BillingCadence;
};

export const PlanSummary = (props: PlanSummaryProps) => {
  const { t } = useTranslation();
  const {
    activeCorePlatformPlan,
    activeAutomationPlan,
    activeMarketingPlan,
    currentSelectedCorePlatformPlan,
    currentSelectedAutomationPlan,
    currentSelectedMarketingPlan,
    numUsersForCalculation,
    currentNumUsersForCalculation,
    pendingNumUsersForCalculation,
    annualDiscountPercentage,
    showAnnualBilling,
    currentCadence,
    upcomingCadence,
    onUpcomingCadenceChange,
    showUpcoming,
    canSwitchCadenceOnly,
    onRequestCadenceChange,
    planChangeClarification,
    newBillingCycleStartDate,
    proratedAmount,
    summaryTotal,
    previewedCadence,
    isDirty,
    pendingRecalculation,
    isAwaitingPreview,
    onCancelUpdate,
    onUpdatePlan,
    hasPendingChange,
    pendingCorePlatformPlan,
    pendingAutomationPlan,
    pendingMarketingPlan,
    pendingPlanDiffers,
    upcomingPendingHasPaidPlan,
    onCancelPendingPlan,
    onChangePendingBillingCadence,
    pendingEffectiveDate,
    pendingCadence,
  } = props;

  const pendingCadenceChanges = pendingCadence !== currentCadence;

  const upcomingAllFree =
    (currentSelectedCorePlatformPlan?.price?.amount ?? 0) === 0 &&
    (currentSelectedAutomationPlan?.price?.amount ?? 0) === 0 &&
    (currentSelectedMarketingPlan?.price?.amount ?? 0) === 0;

  const savesLabel =
    annualDiscountPercentage > 0 && !pendingRecalculation
      ? t('Saves {{percent}}%', {
          percent: String(annualDiscountPercentage),
        })
      : null;

  const annualBillingRadioLabel = savesLabel
    ? `${t('Annual billing')} (${savesLabel})`
    : t('Annual billing');

  const noEffectiveChange = !isDirty && upcomingCadence === currentCadence;

  const updateCancelButtons = (
    <div className="flex justify-center gap-[20px]">
      <Button
        variant="text"
        color="secondary"
        maxWidth={false}
        disabled={noEffectiveChange}
        onClick={onCancelUpdate}
        qa={{ id: 'cancel-update' }}
      >
        {t('Cancel Update')}
      </Button>
      <Button
        variant="standard"
        disabled={noEffectiveChange}
        onClick={onUpdatePlan}
        qa={{ id: 'update-plan' }}
      >
        {t('Update Plan')}
      </Button>
    </div>
  );

  return (
    <ContentCard loadable>
      <PlanSummaryCard
        sectionName={t('Current')}
        cadence={currentCadence}
        showAnnualBilling={showAnnualBilling}
        corePlatformPlan={activeCorePlatformPlan}
        numUsersForCalculation={currentNumUsersForCalculation}
        automationPlan={activeAutomationPlan}
        marketingPlan={activeMarketingPlan}
      />

      {showAnnualBilling && !showUpcoming && canSwitchCadenceOnly ? (
        <>
          <Spacer size={20} />
          <div className="flex flex-col items-center">
            <Button
              variant="text"
              color="secondary"
              maxWidth={false}
              onClick={onRequestCadenceChange}
              qa={{ id: 'change-billing-cadence' }}
            >
              {currentCadence === 'year'
                ? t('Change to Monthly Billing')
                : t('Change to Annual Billing')}
            </Button>
            {currentCadence !== 'year' && savesLabel ? (
              <Typography size="sm" color="font-secondary" className="italic">
                {savesLabel}
              </Typography>
            ) : null}
          </div>
        </>
      ) : null}

      {(!showUpcoming || isAwaitingPreview) && hasPendingChange ? (
        <>
          <Spacer size={30} />
          <div className="relative">
            <hr className="my-0 border-button-tertiary-default" />
            {pendingEffectiveDate ? (
              <div className="absolute top-[1px] right-0 left-0 flex items-center justify-center">
                <div className="bg-feedback-toast-warning py-2 px-4 uppercase max-w-[90%]">
                  <Typography
                    size="sm"
                    weight="semibold"
                    color="font-white"
                    truncate
                  >
                    {pendingCadenceChanges
                      ? t('Pending {{cadence}} Plan ({{date}})', {
                          cadence: cadenceLabel(pendingCadence, t),
                          date: formatPendingDate(pendingEffectiveDate),
                        })
                      : t('Pending Plan ({{date}})', {
                          date: formatPendingDate(pendingEffectiveDate),
                        })}
                  </Typography>
                </div>
              </div>
            ) : null}
          </div>
          <Spacer size={30} />
          <Spacer size={15} />
          <PlanSummaryCard
            sectionName={t('Upcoming')}
            cadence={pendingCadence}
            showAnnualBilling={showAnnualBilling}
            corePlatformPlan={pendingCorePlatformPlan}
            numUsersForCalculation={pendingNumUsersForCalculation}
            automationPlan={pendingAutomationPlan}
            marketingPlan={pendingMarketingPlan}
          />
          <div className="flex flex-col items-center gap-5 pt-[20px]">
            <Button
              variant="text"
              color="warning"
              maxWidth={false}
              onClick={onCancelPendingPlan}
              qa={{ id: 'cancel-pending-plan-summary' }}
            >
              {t('Cancel Pending Plan')}
            </Button>
            {showAnnualBilling && pendingPlanDiffers && upcomingPendingHasPaidPlan ? (
              <Button
                variant="text"
                color="secondary"
                maxWidth={false}
                onClick={onChangePendingBillingCadence}
                qa={{ id: 'change-pending-billing-cadence' }}
              >
                {pendingCadence === 'year'
                  ? t('Change to Monthly Billing')
                  : t('Change to Annual Billing')}
              </Button>
            ) : null}
          </div>
        </>
      ) : null}

      {showUpcoming && !isAwaitingPreview ? (
        <>
          <Spacer size={30} />
          <hr className="my-0 border-button-tertiary-default" />
          <Spacer size={30} />

          {!upcomingAllFree ? (
            <>
              <Typography weight="semibold" size="lg">
                {showAnnualBilling
                  ? t('Upcoming Plan Summary ({{period}})', {
                      period: cadenceLabel(upcomingCadence, t),
                    })
                  : t('Upcoming Plan Summary')}
              </Typography>
              <Spacer size={15} />
              {showAnnualBilling ? (
                <div className="flex flex-col [&_label]:!my-[6px]">
                  <RadioButton
                    hover={false}
                    size="small"
                    name="upcoming-cadence"
                    value="month"
                    label={t('Monthly billing')}
                    checked={upcomingCadence === 'month'}
                    onChange={() => onUpcomingCadenceChange('month')}
                    dataQa="upcoming-cadence-monthly"
                  />
                  <RadioButton
                    hover={false}
                    size="small"
                    name="upcoming-cadence"
                    value="year"
                    label={annualBillingRadioLabel}
                    checked={upcomingCadence === 'year'}
                    onChange={() => onUpcomingCadenceChange('year')}
                    dataQa="upcoming-cadence-annual"
                  />
                </div>
              ) : null}
              <Spacer size={20} />
              <UpcomingLines
                cadence={upcomingCadence}
                corePlatformPlan={currentSelectedCorePlatformPlan}
                automationPlan={currentSelectedAutomationPlan}
                marketingPlan={currentSelectedMarketingPlan}
                numUsersForCalculation={numUsersForCalculation}
                proratedAmount={proratedAmount}
                firstBillingDate={newBillingCycleStartDate}
                summaryTotal={summaryTotal}
                previewedCadence={previewedCadence}
              />
            </>
          ) : (
            <PlanSummaryCard
              sectionName={t('Upcoming')}
              cadence={upcomingCadence}
              showAnnualBilling={showAnnualBilling}
              corePlatformPlan={currentSelectedCorePlatformPlan}
              numUsersForCalculation={numUsersForCalculation}
              automationPlan={currentSelectedAutomationPlan}
              marketingPlan={currentSelectedMarketingPlan}
              proratedAmount={proratedAmount}
              firstBillingDate={newBillingCycleStartDate}
            />
          )}

          {planChangeClarification ? (
            <>
              <Spacer size={30} />
              <Typography size="md" className="text-center">
                {ensureTrailingPeriod(planChangeClarification)}
              </Typography>
            </>
          ) : null}

          {showAnnualBilling ? (
            <>
              <Spacer size={30} />
              {updateCancelButtons}
            </>
          ) : null}
        </>
      ) : null}

      {!showAnnualBilling && (isDirty || !hasPendingChange) ? (
        <>
          <Spacer size={30} />
          {updateCancelButtons}
        </>
      ) : null}

      {pendingRecalculation ? (
        <div className="absolute top-0 right-0 left-0 bottom-0 flex items-center justify-center bg-[rgba(255,255,255,0.5)]">
          <Loader loading paddingTop="0" />
        </div>
      ) : null}
    </ContentCard>
  );
};
